import { format } from "date-fns";
import { Plus } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useMutation, useQuery } from "urql";

import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { DestructiveConfirmationAlertDialog } from "@/components/ui/destructive-confirmation-alert-dialog";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useTabSearchParams } from "@/hooks/useTabSearchParams";

import {
    createLayoutTemplateMutation,
    deleteLayoutTemplateMutation,
    layoutTemplatesQuery,
} from "./queries/layoutTemplates";

export default function LayoutTemplatesRoute() {
    const { tab, setTab } = useTabSearchParams();

    return (
        <div className="p-4">
            <PageHeader title="Tournament Layout Templates" />

            <p className="mb-4 text-sm text-gray-600">
                These can be built upon when creating layouts for a tournament. The one marked as
                default will be used for new tournaments automatically.
            </p>

            <Tabs value={tab} onValueChange={setTab} className="w-full">
                <TabsList className="grid w-full grid-cols-3">
                    <TabsTrigger value="before">Before</TabsTrigger>
                    <TabsTrigger value="during">During</TabsTrigger>
                    <TabsTrigger value="after">After</TabsTrigger>
                </TabsList>

                <TabsContent value="before">
                    <PhaseTemplatesSection phase="BEFORE" phaseLabel="Before Tournament" />
                </TabsContent>

                <TabsContent value="during">
                    <PhaseTemplatesSection phase="DURING" phaseLabel="During Tournament" />
                </TabsContent>

                <TabsContent value="after">
                    <PhaseTemplatesSection phase="AFTER" phaseLabel="After Tournament" />
                </TabsContent>
            </Tabs>
        </div>
    );
}

function PhaseTemplatesSection({
    phase,
    phaseLabel,
}: {
    phase: "BEFORE" | "DURING" | "AFTER";
    phaseLabel: string;
}) {
    const navigate = useNavigate();
    const [templateToDelete, setTemplateToDelete] = useState<{ id: string; name: string } | null>(
        null
    );

    const [result, refetch] = useQuery({
        query: layoutTemplatesQuery,
        variables: { phase },
    });

    const [_createTemplateMutation, createTemplate] = useMutation(createLayoutTemplateMutation);
    const [_deleteTemplateMutation, deleteTemplateMutation] = useMutation(
        deleteLayoutTemplateMutation
    );

    const { data, fetching } = result;

    const handleCreate = async () => {
        const { data, error } = await createTemplate({
            input: {
                name: `${phaseLabel} Template`,
                tournamentPhase: phase,
                content: {
                    version: "1.0",
                    blocks: {},
                    order: [],
                },
                isDefault: false,
            },
        });

        if (error) {
            console.error("Failed to create template:", error);
            return;
        }

        if (data?.createTournamentLayoutTemplate) {
            setTimeout(() => {
                navigate(`/tournament-layout-templates/${data.createTournamentLayoutTemplate.id}`);
            }, 100);
        }
    };

    if (fetching) return <div>Loading...</div>;

    const templates = data?.tournamentLayoutTemplates || [];

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between pt-4">
                <h3 className="text-lg font-medium">{phaseLabel} Templates</h3>
                <Button onClick={handleCreate}>
                    <Plus className="mr-2 h-4 w-4" />
                    Create Template
                </Button>
            </div>

            {templates.length === 0 ? (
                <div className="py-8 text-center text-gray-500">
                    No templates for "{phaseLabel.toLowerCase()}" phase
                </div>
            ) : (
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>Name</TableHead>
                            <TableHead>Default</TableHead>
                            <TableHead>Created</TableHead>
                            <TableHead>Updated</TableHead>
                            <TableHead>Actions</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {templates.map(template => (
                            <TableRow key={template.id}>
                                <TableCell className="font-medium">{template.name}</TableCell>
                                <TableCell>
                                    {template.isDefault ? (
                                        <span className="rounded bg-green-100 px-2 py-1 text-xs text-green-800">
                                            Default
                                        </span>
                                    ) : (
                                        <span className="text-gray-400">-</span>
                                    )}
                                </TableCell>
                                <TableCell>
                                    {format(new Date(template.createdAt), "MMM d, yyyy")}
                                </TableCell>
                                <TableCell>
                                    {format(new Date(template.updatedAt), "MMM d, yyyy")}
                                </TableCell>
                                <TableCell>
                                    <div className="flex gap-2">
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() =>
                                                navigate(
                                                    `/tournament-layout-templates/${template.id}`
                                                )
                                            }
                                        >
                                            Edit
                                        </Button>
                                        <Button
                                            variant="destructive"
                                            size="sm"
                                            onClick={() => {
                                                setTemplateToDelete({
                                                    id: template.id,
                                                    name: template.name,
                                                });
                                            }}
                                        >
                                            Delete
                                        </Button>
                                    </div>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            )}

            <DestructiveConfirmationAlertDialog
                open={templateToDelete !== null}
                onOpenChange={open => {
                    if (!open) {
                        setTemplateToDelete(null);
                    }
                }}
                onConfirm={async () => {
                    if (!templateToDelete) return;

                    const { error } = await deleteTemplateMutation({
                        input: { id: templateToDelete.id },
                    });

                    if (error) {
                        // Check if it's a default template error or other error
                        if (error.message.includes("Cannot delete a default template")) {
                            toast.error("Cannot delete default template", {
                                description: "Please set another template as default first.",
                            });
                        } else {
                            toast.error("Failed to delete template", {
                                description: error.message || "An unexpected error occurred",
                            });
                        }
                    } else {
                        toast.success("Template deleted successfully");
                        refetch();
                    }

                    setTemplateToDelete(null);
                }}
                title="Delete Template"
                description={`Are you sure you want to delete "${templateToDelete?.name}"? This action cannot be undone.`}
            />
        </div>
    );
}
